<script setup lang="ts">
import MainLayout from "@/layouts/MainLayout.vue";
import client from "@/utils/axios";
import { computed, onUnmounted, ref, watch } from "vue";
import { useRoute } from "vue-router";
import AbnahmeView from "./AbnahmeView.vue";
import QcView from "./QcView.vue";
import RegularView from "./RegularView.vue";
import { createClient } from "@supabase/supabase-js";
import { ImageBatch } from "@/types";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import PhaseSidemenu from "@/components/Sidebar/Redesign/PhaseSidemenu.vue";
import { useUserStore } from "@/stores/user";

const route = useRoute();
const userStore = useUserStore();

const phaseData = ref();
const phaseStatusId = ref(null);
const isLoading = ref(false);
const isAddImageModalOpen = ref(false);
const isBaumappeOpen = ref(false);

const selectedPhase = ref();
let currentLanguage = computed(() => userStore.language);
let lastDatabaseTranslationUpdateTimestamp = computed(
  () => userStore.translationsUpdateTimestamp
);

let supabase = createClient(
  process.env.VUE_APP_SUPABASE_URL || "",
  process.env.VUE_APP_SUPABASE_API_KEY || ""
);

let batches = ref<Array<ImageBatch>>([]);

const toggleIsAddImageModalOpen = (isOpen: boolean) => {
  isAddImageModalOpen.value = isOpen;
};

const toggleBaumappe = (isOpen: boolean) => {
  isBaumappeOpen.value = isOpen;
};

// Fetch data
const getPhaseDetails = async () => {
  const projectId = route.params.id;
  const phaseId = route.params.phaseId;
  isLoading.value = true;

  try {
    const response = await client.get(
      `/projects/${projectId}/phase/${phaseId}?locale=${currentLanguage.value}`
    );
    phaseData.value = response.data;
    batches.value = response.data.batches;
    let tempPhase = response.data.phases.find(
      (item: any) => Number(item.phaseId) === Number(phaseId)
    );

    phaseStatusId.value = tempPhase?.phaseStatus?.id;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const handleImagesChanges = (payload: any) => {
  let newBatchImage = payload.new;
  if (payload.eventType === "INSERT") {
    let tempBatch: any = batches.value.find(
      (item: any) => item.id === newBatchImage.images_batch
    );
    if (tempBatch) {
      tempBatch.images.push(newBatchImage);
    }
  }
  if (payload.eventType === "UPDATE") {
    let tempBatch: any = batches.value.find(
      (item: any) => item.id === newBatchImage.images_batch
    );
    if (tempBatch) {
      let tempImage = tempBatch.images.find(
        (image: any) => image.id === newBatchImage.id
      );
      if (tempImage) {
        tempImage.status = newBatchImage.status;
        tempImage.internet_speed = newBatchImage.internet_speed;
      }
    }
  }
};

const salesforceLink = computed(() => {
  if (phaseData.value) {
    let phase = phaseData.value?.phases.find(
      (phase: any) => phase.phaseId === route.params.phaseId
    );
    if (phase) {
      return `https://smalt.lightning.force.com/lightning/r/Phase__c/${phase.salesforceId}/view`;
    }
    return null;
  }
  return null;
});

const handleImagesBatchChanges = (payload: any) => {
  if (payload.eventType === "INSERT") {
    let newBatch = payload.new;
    newBatch.images = [];
    batches.value.push(newBatch);
  }
};

watch(
  () => route.params.phaseId, // Watch for the 'id' param change in the route
  async (newId, oldId) => {
    if (newId !== oldId) {
      await getPhaseDetails();

      let currentPhaseProgress = phaseData.value.phases.find(
        (item: any) => item.phaseId === Number(newId)
      );

      if (currentPhaseProgress) {
        supabase
          .channel("images_batch")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "installation_images_batch",
              filter: `phase_progress_id=eq.${currentPhaseProgress?.phaseProgressId}`,
            },
            handleImagesBatchChanges
          )
          .subscribe();
        supabase
          .channel("images")
          .on(
            "postgres_changes",
            {
              event: "*",
              schema: "public",
              table: "installation_images",
              filter: `phase_progress_id=eq.${currentPhaseProgress?.phaseProgressId}`,
            },
            handleImagesChanges
          )
          .subscribe();
      } else {
        console.error("Cannot find phase progress id.");
      }
      selectedPhase.value = newId; // Trigger the API call when the route param changes
    }
  },
  { immediate: true } // Immediately run on component mount
);

onUnmounted(() => {
  supabase.removeAllChannels();
});

watch(
  currentLanguage,
  async (newLang: any, oldLang: any) => {
    if (newLang && oldLang && newLang !== oldLang) {
      await getPhaseDetails();
    }
  },
  { immediate: true }
);

watch(
  lastDatabaseTranslationUpdateTimestamp,
  async (newTimestamp: any, oldTimestamp: any) => {
    if (newTimestamp && oldTimestamp && newTimestamp !== oldTimestamp) {
      await getPhaseDetails();
    }
  },
  { immediate: true }
);
</script>

<template>
  <MainLayout>
    <SecondaryPageHeader
      :phase-details="phaseData"
      :salesforce-link="salesforceLink"
      :is-baumappe-open="isBaumappeOpen"
      :has-baumappe="phaseData?.project?.baummape !== null"
      @toggle-baumappe="toggleBaumappe"
      @toggle-is-add-image-modal-open="toggleIsAddImageModalOpen"
    />
    <div class="flex h-[calc(100vh-52px)]">
      <PhaseSidemenu
        v-if="phaseData"
        :phase-page="phaseData"
        :is-loading="isLoading"
      />

      <div
        class="flex flex-col h-full items-center justify-center w-full"
        v-if="phaseData"
      >
        <div class="flex w-full h-full" v-if="phaseStatusId === 4">
          <AbnahmeView
            v-if="phaseData"
            :batches="batches"
            :phase-id="Number(route.params.phaseId)"
            :project-id="route.params.id + ''"
            :phase-details="phaseData"
            :is-add-image-modal-open="isAddImageModalOpen"
            :is-baumappe-open="isBaumappeOpen"
            :has-baumappe="phaseData?.project?.baummape !== null"
            @toggle-baumappe="toggleBaumappe"
            @toggle-is-add-image-modal-open="toggleIsAddImageModalOpen"
            @refresh="getPhaseDetails"
          />
        </div>
        <div class="flex w-full h-full" v-if="phaseStatusId === 5">
          <QcView
            v-if="phaseData"
            :batches="batches"
            :phase-id="Number(route.params.phaseId)"
            :project-id="route.params.id + ''"
            :phase-details="phaseData"
            :is-add-image-modal-open="isAddImageModalOpen"
            :is-baumappe-open="isBaumappeOpen"
            :has-baumappe="phaseData?.project?.baummape !== null"
            @toggle-baumappe="toggleBaumappe"
            @toggle-is-add-image-modal-open="toggleIsAddImageModalOpen"
            @refresh="getPhaseDetails"
          />
        </div>

        <div
          class="flex w-full h-full"
          v-if="phaseStatusId !== 4 && phaseStatusId !== 5"
        >
          <RegularView
            v-if="phaseData"
            :batches="batches"
            :phase-id="Number(route.params.phaseId)"
            :phase-details="phaseData"
            :is-add-image-modal-open="isAddImageModalOpen"
            :is-baumappe-open="isBaumappeOpen"
            :has-baumappe="phaseData?.project?.baummape !== null"
            @toggle-baumappe="toggleBaumappe"
            @toggle-is-add-image-modal-open="toggleIsAddImageModalOpen"
            @refresh="getPhaseDetails"
          />
        </div>
      </div>
    </div>
  </MainLayout>
</template>
